/**
 * TopicPage
 *
 * One page component covering all four topic views (Parasite Rate, Parasite
 * Incidence, Insecticide Resistance, Seasonality). The active topic is derived
 * from the URL path (/articles/topic/:slug) via the shared route mapping, so a
 * single component serves every topic and re-wires itself when the slug changes.
 *
 * The topic restricts the fetch to that topic's articles (applied by the shared
 * context's fetchPage based on the resolved sidebar option).
 */

import { useMemo } from "react"
import { useLocation } from "react-router-dom"
import JournalArticlesShell from "@/components/journal-articles/JournalArticlesShell"
import JournalArticlesTable, {
  buildCollectionFilterOptions,
} from "@/components/journal-articles/JournalArticlesTable"
import {
  useJournalArticlesPage,
  stripLocationColumns,
} from "@/components/journal-articles/useJournalArticlesPage"
import { pathToSidebarOption } from "@/components/journal-articles/sidebarRoutes"
import {
  SidebarOption,
  getSidebarOptionLabel,
} from "@/components/journal-articles/JournalArticlesSidebar"

/** Fallback heading if the topic label cannot be resolved. */
const FALLBACK_TITLE: string = "Journal Articles"

/** Short description shown under the page heading. */
const PAGE_DESCRIPTION: string = "Browse journal articles that have been labelled with a particular data topic"

/**
 * The Topic page (serves all four topics via the URL slug).
 *
 * @returns The rendered page element.
 */
export default function TopicPage(): React.JSX.Element {
  const location = useLocation()

  // Resolve the topic sidebar option from the URL path. Using the shared route
  // mapping keeps the slug<->option convention in one place.
  const sidebarOption: SidebarOption = pathToSidebarOption(location.pathname)
  const title: string = getSidebarOptionLabel(sidebarOption) ?? FALLBACK_TITLE

  const page = useJournalArticlesPage({ sidebarOption })

  const filterOptions = useMemo(
    () => buildCollectionFilterOptions(page.collections),
    [page.collections]
  )

  const visibleColumns = useMemo(
    () => stripLocationColumns(page.visibleColumns),
    [page.visibleColumns]
  )

  return (
    <JournalArticlesShell
      title={title}
      description={PAGE_DESCRIPTION}
      showDetailsPanel
      searchValue={page.searchQuery}
      onSearchChange={page.setSearchQuery}
    >
      <JournalArticlesTable page={page} filterOptions={filterOptions} visibleColumns={visibleColumns} />
    </JournalArticlesShell>
  )
}
